import { useEffect, useRef } from 'react'

import { extractValidationFieldErrors } from '@/utils/extract-validation-detail'

/**
 * Map a server 422 field error on `code` onto the form's code field, per
 * `.claude/rules/forms.md`. The realistic coupon 422 is a duplicate code — the
 * one rule the client can't mirror (it doesn't know existing codes). Other field
 * errors stay with the caller's generic mutation banner (client zod mirrors
 * those). Nothing is swallowed either way.
 *
 * `setCodeError` is read through a ref so the effect depends only on the error
 * identity — an inline arrow at the call site won't re-fire it every render.
 */
export const useCouponCodeServerError = (
  serverError: unknown,
  setCodeError: (message: string) => void,
): void => {
  const setCodeErrorRef = useRef(setCodeError)
  setCodeErrorRef.current = setCodeError

  useEffect(
    function mapCodeServerError() {
      const fieldErrors = extractValidationFieldErrors(serverError)
      const codeError = fieldErrors?.find((entry) => entry.field === 'code')
      if (codeError !== undefined) {
        setCodeErrorRef.current(codeError.message)
      }
    },
    [serverError],
  )
}
